import type { BatchRowKind, IngestJobDTO, IngestJobState } from '@contracts/ingest';

/** §8.1: the card's module groups, in this order; `noun` counts them in the head ("4 việc · 1 đối tác"). */
export const BATCH_GROUPS: readonly { label: string; noun: string; kinds: readonly BatchRowKind[] }[] = [
  { label: 'Việc', noun: 'việc', kinds: ['task', 'note'] },
  { label: 'Đối tác', noun: 'đối tác', kinds: ['partner', 'contact'] },
  { label: 'Bên liên quan', noun: 'bên liên quan', kinds: ['stakeholder', 'participant', 'contract'] },
  { label: 'Tài chính', noun: 'khoản tài chính', kinds: ['finance_entry'] },
  { label: 'Chiến lược', noun: 'mục chiến lược', kinds: ['metric_value', 'review'] },
];

/** §8.1 the row pills: the op, a note row, and the two statuses that need the person. */
export const OP_LABEL = {
  create: 'Tạo mới', update: 'Cập nhật', note: 'Ghi chú', duplicate: 'Nghi trùng', needs_fix: 'Cần sửa',
  rejected: 'Không đưa vào',
} as const;

/** D119: a document's name, the pasted text, or the pictures picked together. */
export function batchTitle(job: Pick<IngestJobDTO, 'sourceKind' | 'documents'>): string {
  switch (job.sourceKind) {
    case 'text': return 'Đoạn văn đã dán';
    case 'images': return `Ảnh (${job.documents.length} ảnh)`;
    case 'file': return job.documents[0]?.filename ?? 'Tài liệu';
  }
}

type ByKind = Partial<Record<BatchRowKind, number>>;
const groupCount = (byKind: ByKind, kinds: readonly BatchRowKind[]) => kinds.reduce((n, k) => n + (byKind[k] ?? 0), 0);

/** The rows the card shows (rejected ones are not in `byKind`). */
export const rowTotal = (byKind: ByKind): number => BATCH_GROUPS.reduce((n, g) => n + groupCount(byKind, g.kinds), 0);

/** The card head's second line, group by group in the §8.1 order. */
export function countsLine(byKind: ByKind): string {
  return BATCH_GROUPS.map((g) => [groupCount(byKind, g.kinds), g.noun] as const)
    .filter(([n]) => n > 0).map(([n, noun]) => `${n} ${noun}`).join(' · ');
}

/** D118 ★: Hana's line above a ready card, in her self-name and the person's address. */
export function leadSentence(selfName: string, n: number, address: string, approvable: number): string {
  const self = selfName.charAt(0).toLocaleUpperCase('vi') + selfName.slice(1);
  // D146: over a "Duyệt 0" footer, inviting approval contradicts the card.
  if (approvable === 0) return `${self} đọc xong rồi. Có ${n} mục nhưng chưa mục nào duyệt được, ${address} xem lại nhé.`;
  return `${self} đọc xong rồi. Có ${n} mục, ${address} xem và duyệt một lần nhé.`;
}

/** The one muted line of a job that ended without a batch to approve. */
export function stateLine(state: Extract<IngestJobState, 'cancelled' | 'discarded' | 'expired'>): string {
  switch (state) {
    case 'cancelled': return 'Đã hủy đọc tài liệu này';
    case 'discarded': return 'Đã bỏ lô này';
    case 'expired': return 'Lô này đã hết hạn';
  }
}
